import { Box, Group, SimpleGrid, Stack, Text } from "@mantine/core"
import { ReactNode } from "react"
import classes from "./order-ledger.module.css"

type Stat = { value: number | string; label: string }
/** `muted` / `negative` are kept for callers; the canvas renders every row the same (no red, no indent). */
type Row = { label: string; value: string; muted?: boolean; negative?: boolean }
/** `icon` is kept for callers; the canvas shows plain label/value rows. */
type PayRow = { icon?: ReactNode; label: string; value: string }

type Props = {
  stats?: Stat[]
  headLabel?: string
  /** Kept for callers; not rendered (the canvas heading is plain text). */
  headIcon?: ReactNode
  rows: Row[]
  grand: { label: string; value: string }
  payRows?: PayRow[]
  /** Ord_PurchaseDetail: tight rows, no dotted separators, regular-weight values. */
  compact?: boolean
}

/**
 * Order summary ledger (OrdB_SupplierOrder «Захиалгын нэгтгэл», right half):
 * grey stat strip, «Тооцоолол» rows with dotted separators, bold grand total,
 * then the payment-confirmation rows. Money in the theme monospace font.
 */
export function OrderLedger({ stats, headLabel, rows, grand, payRows, compact = false }: Props) {
  return (
    <Stack gap="sm" data-compact={compact || undefined} className={classes.root}>
      {stats && stats.length > 0 && (
        <SimpleGrid cols={stats.length} spacing="md" className={classes.statStrip}>
          {stats.map((stat, i) => (
            <Box key={i}>
              <Text ff="monospace" className={classes.statValue}>{stat.value}</Text>
              <Text className={classes.statLabel}>{stat.label}</Text>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {headLabel && <Text className={classes.head}>{headLabel}</Text>}

      <Box>
        {rows.map((row, i) => (
          <Group key={i} justify="space-between" wrap="nowrap" gap="md" className={classes.row}>
            <Text className={classes.label}>{row.label}</Text>
            <Text ff="monospace" className={classes.value}>{row.value}</Text>
          </Group>
        ))}
      </Box>

      <Group justify="space-between" wrap="nowrap" gap="md" className={classes.grand}>
        <Text className={classes.grandLabel}>{grand.label}</Text>
        <Text ff="monospace" className={classes.grandValue}>{grand.value}</Text>
      </Group>

      {payRows && payRows.length > 0 && (
        <Stack gap="xs">
          {payRows.map((pay, i) => (
            <Group key={i} justify="space-between" wrap="nowrap" gap="md">
              <Text className={classes.label}>{pay.label}</Text>
              <Text ff="monospace" className={classes.value}>{pay.value}</Text>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
